import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Plus, Search } from "lucide-react";
import usePageTitle from "@/hooks/use-page-title";
import { useAuth } from "@/context/AuthContext";
import { canSeeAmount } from "@/lib/roles";

const STATUS_COLORS = {
  draft: "#71717A", sent: "#0EA5E9", approved: "#22C55E",
  rejected: "#EF4444", converted: "#FF6B00",
};

export default function Quotations() {
  usePageTitle("Quotations");
  const { user } = useAuth();
  const showAmt = canSeeAmount(user);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    const params = status ? { status } : {};
    api.get("/quotations", { params }).then((r) => setRows(r.data));
  }, [status]);

  const filtered = rows.filter((r) => !q ||
    (r.id.toLowerCase().includes(q.toLowerCase()) ||
      (r.customer_name || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-6" data-testid="quotations-root">
      <div className="flex items-end justify-between border-b border-[hsl(var(--border))] pb-6">
        <div>
          <div className="overline mb-2">Pre-sale</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Quotations</h1>
        </div>
        {user?.role !== "customer" && (
          <Link to="/quotations/new" className="btn-swiss btn-accent" data-testid="new-quotation-btn">
            <Plus size={14} /> New Quotation
          </Link>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 flex-1 min-w-[280px] max-w-md">
          <Search size={16} className="text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by ID or customer…"
            className="flex-1 bg-transparent px-3 py-2 text-sm outline-none" data-testid="quotation-search-input" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none" data-testid="quotation-status-filter">
          <option value="">All statuses</option>
          {Object.keys(STATUS_COLORS).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="card-swiss overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[hsl(var(--border))]">
              <th className="px-4 py-3 text-left overline">Quote #</th>
              <th className="px-4 py-3 text-left overline">Customer</th>
              <th className="px-4 py-3 text-left overline">Date</th>
              {showAmt && <th className="px-4 py-3 text-right overline">Amount</th>}
              <th className="px-4 py-3 text-left overline">Status</th>
              <th className="px-4 py-3 text-left overline">Order</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]" data-testid={`quotation-row-${r.id}`}>
                <td className="px-4 py-3"><Link to={`/quotations/${r.id}`} className="font-mono text-xs font-semibold hover:text-[#FF6B00]">{r.id}</Link></td>
                <td className="px-4 py-3">
                  <div className="font-semibold">{r.customer_name}</div>
                  {r.customer_company && <div className="text-xs text-muted-foreground">{r.customer_company}</div>}
                </td>
                <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{r.quote_date}</td>
                {showAmt && <td className="px-4 py-3 text-right font-mono">₹{Math.round(r.grand_total || 0).toLocaleString("en-IN")}</td>}
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-2 border px-2 py-1 text-xs font-semibold uppercase tracking-wider"
                    style={{ borderColor: STATUS_COLORS[r.status], color: STATUS_COLORS[r.status] }}>
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {r.order_id
                    ? <Link to={`/orders/${r.order_id}`} className="text-xs font-mono text-[#FF6B00]">{r.order_id}</Link>
                    : <span className="text-xs text-muted-foreground">—</span>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={showAmt ? 6 : 5} className="px-4 py-12 text-center text-muted-foreground">No quotations found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
